// ============================================================================
// KLINISOL CRO - Therapeutic Areas Page (REGULATORY COMPLIANT VERSION)
// Honest, focused, evidence-based approach
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// ============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { 
    Activity, 
    Heart, 
    Stethoscope,
    CheckCircle,
    ArrowRight,
    Shield,
    FileCheck,
    Users
} from 'lucide-react';

const TherapeuticAreas = () => {
    return (
        <div className="min-h-screen bg-[#f8fafd]">
            {/* Hero Section */}
            <section className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-16 px-6">
                <div className="max-w-7xl mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">
                        Clinical Research Support Capabilities
                    </h1>
                    <p className="text-xl max-w-3xl mx-auto">
                        FDA-ready operational excellence for investigator-initiated trials
                    </p>
                </div>
            </section>

            {/* Core Value Proposition */}
            <section className="py-16 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="max-w-4xl mx-auto text-center mb-12">
                        <h2 className="text-3xl font-bold text-[#475569] mb-6">
                            What Makes KLIN<span className="text-[#E91E63]">i</span>SOL Different
                        </h2>
                        <p className="text-lg text-gray-700 leading-relaxed">
                            Unlike large CROs, we specialize in supporting investigator-initiated research 
                            with lean, efficient, and fully compliant operations. Our focus is on 
                            <strong> regulatory quality, not volume</strong> — enabling smaller teams to 
                            conduct audit-ready clinical trials without the overhead of enterprise CROs.
                        </p>
                    </div>

                    {/* Three Pillars */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                        <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6 text-center hover:border-[#E91E63] transition-all">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E91E63]/10 flex items-center justify-center">
                                <Shield className="w-8 h-8 text-[#E91E63]" />
                            </div>
                            <h3 className="text-xl font-bold text-[#475569] mb-3">
                                FDA 21 CFR Part 11
                            </h3>
                            <p className="text-gray-700">
                                Complete audit trail, electronic signatures, and data integrity 
                                controls built into every process.
                            </p>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6 text-center hover:border-[#E91E63] transition-all">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#475569]/10 flex items-center justify-center">
                                <FileCheck className="w-8 h-8 text-[#475569]" />
                            </div>
                            <h3 className="text-xl font-bold text-[#475569] mb-3">
                                ICH-GCP Trained
                            </h3>
                            <p className="text-gray-700">
                                All staff maintain current ICH-GCP certification and receive 
                                ongoing training in current regulations.
                            </p>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6 text-center hover:border-[#E91E63] transition-all">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E91E63]/10 flex items-center justify-center">
                                <Users className="w-8 h-8 text-[#E91E63]" />
                            </div>
                            <h3 className="text-xl font-bold text-[#475569] mb-3">
                                Investigator Focus
                            </h3>
                            <p className="text-gray-700">
                                Purpose-built processes for investigator-initiated studies — 
                                not adapted from sponsor-driven models.
                            </p>
                        </div>
                    </div>

                    {/* Capabilities Section */}
                    <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
                        <h2 className="text-2xl font-bold text-[#475569] mb-6 text-center">
                            Trial Support Services
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                            {/* Regulatory & Compliance */}
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <CheckCircle className="w-6 h-6 text-[#E91E63]" />
                                    Regulatory & Compliance
                                </h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start gap-3">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span>FDA 21 CFR Part 11-ready systems</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span>ICH-GCP training and documentation</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span>Electronic signature workflows</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span>Complete audit trail generation</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span>SOP library (21+ procedures)</span>
                                    </li>
                                </ul>
                            </div>

                            {/* Operational Support */}
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <CheckCircle className="w-6 h-6 text-[#E91E63]" />
                                    Operational Support
                                </h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start gap-3">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span>Protocol review and refinement</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span>Regulatory submission assistance</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span>Data management and quality control</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span>Site training and oversight</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-[#E91E63] font-bold">•</span>
                                        <span>Safety reporting guidance</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Disclaimer */}
                        <div className="bg-white border-2 border-gray-200 p-4 rounded-lg hover:border-[#475569] hover:shadow-md transition-all duration-300">
                            <p className="text-sm text-gray-700">
                                <strong className="text-[#475569]">Note:</strong> Specific capabilities vary by study phase, therapeutic indication, 
                                and protocol requirements. We recommend scheduling a protocol review to assess fit and 
                                feasibility for your specific trial.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Therapeutic Areas - Simplified */}
            <section className="py-16 px-6 bg-gradient-to-br from-gray-50 to-white">
                <div className="max-w-7xl mx-auto">
                    <div className="max-w-4xl mx-auto text-center mb-12">
                        <h2 className="text-3xl font-bold text-[#475569] mb-6">
                            Therapeutic Area Experience
                        </h2>
                        <p className="text-lg text-gray-700">
                            We support investigator-initiated trials across multiple therapeutic areas. 
                            Our team brings operational expertise in:
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Dermatology */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg hover:border-[#E91E63] transition-all">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 rounded-lg bg-[#E91E63]/10 flex items-center justify-center">
                                    <Activity className="w-6 h-6 text-[#E91E63]" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900">
                                    Dermatology
                                </h3>
                            </div>
                            <p className="text-gray-700 text-sm mb-4">
                                Supporting dermatology investigators in Phase II-IV studies with 
                                regulatory-compliant operations and documentation.
                            </p>
                            <p className="text-xs text-gray-600 italic">
                                Common indications: Psoriasis, Eczema, Acne, Skin Cancer
                            </p>
                        </div>

                        {/* Cardiovascular */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg hover:border-[#E91E63] transition-all">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 rounded-lg bg-[#E91E63]/10 flex items-center justify-center">
                                    <Heart className="w-6 h-6 text-[#E91E63]" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900">
                                    Cardiovascular
                                </h3>
                            </div>
                            <p className="text-gray-700 text-sm mb-4">
                                Experience supporting cardiovascular trials with safety monitoring 
                                and regulatory documentation requirements.
                            </p>
                            <p className="text-xs text-gray-600 italic">
                                Common indications: Heart Failure, Arrhythmias, Hypertension
                            </p>
                        </div>

                        {/* Gastroenterology */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg hover:border-[#E91E63] transition-all">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 rounded-lg bg-[#E91E63]/10 flex items-center justify-center">
                                    <Stethoscope className="w-6 h-6 text-[#E91E63]" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900">
                                    Gastroenterology
                                </h3>
                            </div>
                            <p className="text-gray-700 text-sm mb-4">
                                Supporting GI investigators with protocol development, regulatory 
                                submissions, and quality oversight.
                            </p>
                            <p className="text-xs text-gray-600 italic">
                                Common indications: IBD, IBS, GERD, Hepatology
                            </p>
                        </div>
                    </div>

                    {/* Important Notice */}
                    <div className="mt-12 bg-white border-2 border-gray-200 p-6 rounded-lg max-w-4xl mx-auto hover:border-[#E91E63] hover:shadow-md transition-all duration-300">
                        <h4 className="font-bold text-[#475569] mb-2 flex items-center gap-2">
                            <Shield className="w-5 h-5 text-[#E91E63]" />
                            Protocol-Specific Assessment Required
                        </h4>
                        <p className="text-sm text-gray-700">
                            Therapeutic area experience does not guarantee acceptance of all studies. 
                            Each protocol is evaluated individually for operational feasibility, regulatory complexity, 
                            and resource requirements. Contact us for a no-obligation protocol review.
                        </p>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 px-6 bg-gradient-to-br from-[#475569]/10 to-[#E91E63]/10">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl font-bold text-gray-900 mb-4">
                        Let's Discuss Your Protocol
                    </h2>
                    <p className="text-lg text-gray-700 mb-8">
                        Schedule a confidential protocol review to determine if KLIN<span className="text-[#E91E63]">i</span>SOL 
                        is the right operational partner for your investigator-initiated study.
                    </p>
                    <Link 
                        to="/contact" 
                        className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#475569] to-[#334155] text-white font-bold rounded-lg hover:shadow-xl transition-all text-lg group"
                    >
                        <span>Request Protocol Review</span>
                        <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                    </Link>
                    <p className="text-sm text-gray-600 mt-4">
                        No commitment required • Confidential discussion • 30-minute call
                    </p>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-12 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <h3 className="text-2xl font-bold mb-2">
                                KLIN<span className="text-[#E91E63]">i</span>SOL
                            </h3>
                            <p className="text-sm opacity-90">Advancing Investigator-Initiated Research</p>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold mb-3">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><Link to="/" className="hover:text-[#E91E63] transition-colors">Home</Link></li>
                                <li><Link to="/services" className="hover:text-[#E91E63] transition-colors">Services</Link></li>
                                <li><Link to="/about" className="hover:text-[#E91E63] transition-colors">About</Link></li>
                                <li><Link to="/contact" className="hover:text-[#E91E63] transition-colors">Contact</Link></li>
                            </ul>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold mb-3">Therapeutic Areas</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="flex items-center gap-2">
                                    <Activity className="w-4 h-4" />
                                    Dermatology
                                </li>
                                <li className="flex items-center gap-2">
                                    <Heart className="w-4 h-4" />
                                    Cardiovascular
                                </li>
                                <li className="flex items-center gap-2">
                                    <Stethoscope className="w-4 h-4" />
                                    Gastroenterology
                                </li>
                            </ul>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold mb-3">Contact</h4>
                            <p className="text-sm opacity-90 mb-1">600 Broadway Ste 200 #2189</p>
                            <p className="text-sm opacity-90 mb-2">Albany, NY 12207</p>
                            <p className="text-sm opacity-90 mb-1">Email: info@klinisol.com</p>
                            <p className="text-sm opacity-90">Website: klinisol.com</p>
                        </div>
                    </div>
                    
                    <div className="border-t border-white/20 pt-6 text-center">
                        <p className="text-sm opacity-90 mb-4">&copy; 2025 KLINISOL. All rights reserved.</p>
                        
                        {/* Social Media Links */}
                        <div className="flex items-center justify-center gap-4">
                            <a 
                                href="https://www.linkedin.com/company/klinisol/" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110"
                                aria-label="LinkedIn"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                </svg>
                            </a>
                            
                            <a 
                                href="https://youtube.com/@klinisol721" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110"
                                aria-label="YouTube"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default TherapeuticAreas;
